// components/TrialExpirationHandler.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { 
  Clock, 
  Crown, 
  CheckCircle, 
  ArrowRight, 
  AlertTriangle,
  Calendar,
  Zap,
  Shield,
  X
} from 'lucide-react';

const TrialExpirationHandler = ({ user, onClose, onUpgrade }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [timeRemaining, setTimeRemaining] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!user?.subscription?.expires_at) return;

    const checkTrialStatus = () => {
      const expiryDate = new Date(user.subscription.expires_at);
      const now = new Date();
      const timeLeft = expiryDate - now;

      if (timeLeft <= 0) {
        setIsExpired(true);
        setTimeRemaining('Expired');
        setShowModal(true);
      } else {
        const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        
        if (days > 0) {
          setTimeRemaining(`${days} day${days > 1 ? 's' : ''} remaining`);
        } else {
          setTimeRemaining(`${hours} hour${hours > 1 ? 's' : ''} remaining`);
        }

        // Show upgrade prompt when less than 1 day remaining
        if (timeLeft < 24 * 60 * 60 * 1000) {
          setShowModal(true);
        }
      }
    };

    checkTrialStatus();
    const interval = setInterval(checkTrialStatus, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [user]);

  const handleUpgrade = () => {
    navigate('/subscription?from=trial-expired');
    if (onUpgrade) onUpgrade();
  };

  const handleContinueTrial = () => {
    setShowModal(false);
    if (onClose) onClose();
  };

  if (!showModal || user?.subscription?.status !== 'trial') return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-lg ${
        darkMode ? 'bg-[#0D1E2E]' : 'bg-white'
      } rounded-md overflow-hidden`}>
        
        {/* Header */}
        <div className={`relative p-6 ${
          isExpired 
            ? 'bg-[#28445F]' 
            : 'bg-[#28445F]'
        } text-white`}>
          
          {!isExpired && (
            <button
              onClick={handleContinueTrial}
              className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
              {isExpired ? (
                <AlertTriangle className="w-8 h-8 text-white" />
              ) : (
                <Clock className="w-8 h-8 text-white" />
              )}
            </div>
            
            <h2 className="text-2xl font-bold mb-2">
              {isExpired ? 'Trial Expired' : 'Trial Ending Soon'}
            </h2>
            
            <p className="text-lg opacity-90">
              {isExpired 
                ? 'Your free trial has ended'
                : `Your trial expires in ${timeRemaining}`
              }
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {isExpired 
                ? 'Upgrade now to continue accessing all premium features'
                : 'Don\'t lose access to premium features. Upgrade now!'
              }
            </p>
          </div>

          {/* Features Reminder */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
              <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                AI-powered stock signals & alerts
              </span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
              <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                Unlimited stock monitoring
              </span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
              <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                Premium technical analysis
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleUpgrade}
              className="w-full bg-[#5AA9F0] text-[#07131F] font-semibold py-3 px-4 rounded-lg font-medium hover:bg-[#8CC4F5] transition-all duration-200 flex items-center justify-center"
            >
              <Crown className="w-5 h-5 mr-2" />
              Upgrade to Premium
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
            
            {!isExpired && (
              <button
                onClick={handleContinueTrial}
                className={`w-full py-3 px-4 rounded-lg font-medium border transition-colors ${
                  darkMode 
                    ? 'border-[#28445F] text-gray-300 hover:bg-[#13283C]' 
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Continue with Trial
              </button>
            )}
          </div>

          {isExpired && (
            <div className={`mt-4 p-3 rounded-lg ${
              darkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-600'
            }`}>
              <p className="text-sm text-center">
                Access to premium features has been suspended. Upgrade to restore full access.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialExpirationHandler;